import { useMemo } from "react";
import { optionsFor, DATE_KEYS } from "./classicFields";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const colName = (n) => { let s = ""; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };

export const cellCss = (st, bg) => ({
  background: bg || st.bg || "#FFFFFF",
  color: st.fc || "#000000",
  fontWeight: st.b ? 700 : 400,
  fontStyle: st.i ? "italic" : "normal",
  textDecoration: st.u ? "underline" : "none",
  fontSize: `${(st.sz || 11) * 4 / 3}px`,
  fontFamily: `"${st.ff || "Calibri"}", Calibri, Arial, sans-serif`,
  textAlign: st.ha === "centerContinuous" ? "center" : st.ha || "left",
  verticalAlign: st.va === "center" ? "middle" : st.va || "bottom",
  whiteSpace: st.wrap ? "pre-wrap" : "nowrap",
  borderTop: st.bt || "1px solid transparent",
  borderBottom: st.bb || "1px solid transparent",
  borderLeft: st.bl || "1px solid transparent",
  borderRight: st.br || "1px solid transparent",
});

const fieldCss = (css) => ({
  font: "inherit", color: "inherit", textAlign: css.textAlign, textDecoration: "none", background: "transparent",
  width: "100%", height: "100%", border: 0, outline: "none", padding: "0 2px", resize: "none",
});

// Answer cell: same look as the sheet cell, typed or picked from the ASE Form's choices
const FieldCell = ({ fieldKey, value, placeholder, css, onChange, testId }) => {
  const opts = optionsFor(fieldKey);
  const fc = fieldCss(css);
  if (opts) {
    return (
      <select value={value || ""} onChange={(e) => onChange(fieldKey, e.target.value)} style={{ ...fc, cursor: "pointer" }} data-testid={testId}>
        <option value="">{placeholder || ""}</option>
        {[...(value && !opts.includes(value) ? [value] : []), ...opts].map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (DATE_KEYS.includes(fieldKey)) return <input type="date" value={value || ""} onChange={(e) => onChange(fieldKey, e.target.value)} style={fc} data-testid={testId} />;
  return <textarea value={value || ""} placeholder={placeholder || ""} onChange={(e) => onChange(fieldKey, e.target.value)} style={{ ...fc, padding: "1px 2px" }} className="placeholder:text-black/50" data-testid={testId} />;
};

// Output-sheet value cell: shows the formula result, typing overrides it
const OutputCell = ({ value, overridden, css, onChange, testId }) => (
  <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={{ ...fieldCss(css), padding: "0 3px", whiteSpace: css.whiteSpace }}
    title={typeof value === "string" && value.length > 40 ? value : undefined} data-testid={testId} data-overridden={overridden ? "yes" : "no"} />
);

// Renders one worksheet of the Classic workbook: exact column widths, row heights, merges, fills, fonts and borders
export const SheetGrid = ({ sheet, valueOf, formData, onChange, onOverride, extraEditable = [] }) => {
  const { cols, rows, cells, styles, merges, cf_blank: cfBlank, fields, editable, name } = sheet;
  const sid = slug(name);
  const { spans, covered } = useMemo(() => {
    const sp = {}; const cov = new Set();
    merges.forEach(([r1, c1, r2, c2]) => {
      let visible = 0;
      for (let r = r1; r <= r2; r++) if (!rows[r - 1]?.hidden) visible++;
      const firstVisible = Array.from({ length: r2 - r1 + 1 }, (_, k) => r1 + k).find((r) => !rows[r - 1]?.hidden);
      if (!firstVisible) return;
      sp[`${firstVisible},${c1}`] = { rs: visible, cs: c2 - c1 + 1, anchor: `${colName(c1)}${r1}` };
      for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) if (!(r === firstVisible && c === c1)) cov.add(`${r},${c}`);
    });
    return { spans: sp, covered: cov };
  }, [merges, rows]);

  return (
    <div className="overflow-auto border border-zinc-300 bg-white shadow-sm max-h-[78vh]" data-testid={`classic-sheet-${sid}`}>
      <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: cols.reduce((a, w) => a + w, 0) }}>
        <colgroup>{cols.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
        <tbody>
          {rows.map((row, ri) => {
            const r = ri + 1;
            if (row.hidden) return null;
            return (
              <tr key={r} style={{ height: row.h }}>
                {cols.map((_, ci) => {
                  const c = ci + 1;
                  if (covered.has(`${r},${c}`)) return null;
                  const span = spans[`${r},${c}`];
                  const ref = span?.anchor || `${colName(c)}${r}`;
                  const cell = cells[ref];
                  const st = cell ? styles[cell.s] : {};
                  const key = editable ? fields[ref] : null;
                  const isOutput = !editable && (cell?.v?.startsWith("=") || extraEditable.includes(ref));
                  const shown = key ? formData[key] : valueOf(ref);
                  const empty = (key ? !formData[key] && !cell?.v : shown === "" || shown == null);
                  const css = cellCss(st, empty && cfBlank[ref] ? cfBlank[ref] : null);
                  return (
                    <td key={c} rowSpan={span?.rs > 1 ? span.rs : undefined} colSpan={span?.cs > 1 ? span.cs : undefined}
                      style={{ ...css, padding: key || isOutput ? 0 : "0 3px", overflow: "hidden", height: span ? undefined : row.h }}
                      title={!key && typeof shown === "string" && shown.length > 40 ? shown : undefined}
                      data-testid={key || isOutput ? undefined : cell?.v != null ? `classic-${sid}-${ref}` : undefined}>
                      {isOutput
                        ? <OutputCell value={shown} overridden={formData.classic_overrides?.[`${name}!${ref}`] != null} css={css} onChange={(v) => onOverride(name, ref, v)} testId={`classic-${sid}-${ref}`} />
                        : key
                        ? <FieldCell fieldKey={key} value={formData[key]} placeholder={cell?.v || ""} css={css} onChange={onChange} testId={`classic-${sid}-${ref}`} />
                        : shown}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
